import { useCustomMutation } from '../../graphql';
import { useQueryClient } from '@tanstack/react-query';
import { useInvalidateConfig } from '../../hooks';
import { useAccessTokenReceived } from './useAccessTokenReceived';
import { useGrippContext } from '../../grippContext';
import { useStorageKeys } from '../../storage';

export const useImpersonateUser = () => {
  const { mutateAsync, isPending } = useCustomMutation({
    query: IMPERSONATE_USER,
  });
  const onAccessTokenReceived = useAccessTokenReceived();
  const queryClient = useQueryClient();
  const invalidateConfig = useInvalidateConfig();
  const { secureStorageHandler } = useGrippContext();
  const storageKeys = useStorageKeys();
  return {
    impersonateUserAsync: async (
      input: any,
      onSettled: () => void,
      onRegister: () => void = () => null
    ) => {
      const existingOldToken = await secureStorageHandler.getItem(
        storageKeys.oldAccessToken
      );
      if (existingOldToken) {
        await onAccessTokenReceived('', true, false);
      }

      const data: any = await mutateAsync({ input });
      const result = data?.authentication?.impersonateUser;

      if (!result?.success) {
        throw new Error(
          result?.message || 'Impersonation request was rejected'
        );
      }
      if (typeof result.accessToken !== 'string' || result.accessToken === '') {
        throw new Error(
          'Impersonation succeeded but no access token was returned'
        );
      }

      await onAccessTokenReceived(result.accessToken);
      await invalidateConfig();
      await queryClient.invalidateQueries({ queryKey: ['currentAccount'] });

      onSettled();
    },
  };
};

const IMPERSONATE_USER = `
  mutation Authentication($input: ImpersonateUserInput!) {
    authentication {
      impersonateUser(input: $input) {
        success
        message
        accessToken
      }
    }
  }
`;
